import React from 'react';
import PropTypes from 'prop-types';
import Autocomplete from 'react-autocomplete';
import axios from '../../../utils/axios';
import globals from '../../../globals/communityGlobals';
import { PostCodeAddressType, mapStringToAddressType } from '../../../enums/index';

const locale = globals.getArea('treatments');

class PostcodeSearch extends React.Component {
  state = {
    value: '',
    isFetching: false,
    addresses: [],
    selection: null,
    currentValue: this.props.oldValue,
    hasError: false,
  };

  getAddressesByPostCodeEndpoint = postcode => {
    return `v1/query/GetPostCodeAddresses?postCode=${postcode}`;
  };

  getAddressesByIdEndpoint = addressId => {
    return `v1/query/GetPostcodeAddressesById?addressId=${addressId}`;
  };

  getAddressDetailsEndpoint = (addressId, addressType) => {
    return `v1/query/GetPostCodeAddressDetails?addressId=${addressId}&addressType=${addressType}`;
  };

  setAddresses = addresses => {
    this.setState({ ...this.state, addresses });
    // click function is a workaround to focus the autocomplete in order to display more addresses menu
    this.autoCompleteRef.current.click();
  };

  extractPostCodeFromDescription = description => {
    const splittedDescription = description.split(',');
    if (splittedDescription.length > 0) {
      return splittedDescription.reverse()[0].trim();
    }
    return null;
  };

  mapAddresses = (addressResponse, value) => {
    return addressResponse.data.addresses.map(address => {
      const postCodeValue =
        mapStringToAddressType(address.type) === PostCodeAddressType.Address
          ? this.extractPostCodeFromDescription(address.description)
          : value;
      return {
        ...address,
        value: postCodeValue,
        type: mapStringToAddressType(address.type),
      };
    });
  };

  mapAddressDetails = (addressResponse, value) => {
    return {
      ...addressResponse.data.addressDetails,
      value,
      type: PostCodeAddressType.Address,
    };
  };

  fetchAddress = async (url, value, resultMapper, onSuccess) => {
    const { jwtToken, onInvalidPostcode, handleApiError } = this.props;
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      this.setState({
        ...this.state,
        isFetching: false,
        hasError: !response.data.isValid,
      });

      if (response.data.isValid && onSuccess) {
        onSuccess(resultMapper(response, value));
      } else {
        this.setState({
          ...this.state,
          addresses: [],
        });
        onInvalidPostcode(!response.data.isValid);
      }
    } catch (error) {
      this.setState({
        ...this.state,
        hasError: true,
      });
      handleApiError(error);
    }
  };

  autoCompleteOnChange = async (event, value) => {
    if (!value) {
      this.setState({
        ...this.state,
        isFetching: false,
        addresses: [],
        value: '',
        currentValue: '',
        hasError: false,
      });

      return;
    }

    const isLongEnough = value.trim().length > 4;

    if (!isLongEnough && value) {
      this.setState({
        ...this.state,
        value,
        currentValue: value,
        hasError: true,
      });
    } else {
      this.setState({
        ...this.state,
        value,
        currentValue: value,
        hasError: false,
        isFetching: true,
      });

      await this.fetchAddress(this.getAddressesByPostCodeEndpoint(value), value, this.mapAddresses, this.setAddresses);
    }
  };

  autoCompleteRef = React.createRef();

  autocompleteOnSelect = async (postCode, item) => {
    const { showOnlyPostcode, onSelect } = this.props;

    if (item.type === PostCodeAddressType.Address) {
      const onSuccessFetchAddress = selectedAddressDetails => {
        onSelect(selectedAddressDetails || null, postCode);
      };

      if (showOnlyPostcode) {
        postCode = this.extractPostCodeFromDescription(item.description) || postCode;
        this.setState({
          ...this.state,
          value: postCode,
        });
        onSelect(postCode);
        return;
      }

      await this.fetchAddress(
        this.getAddressDetailsEndpoint(item.id, item.type),
        item.value,
        this.mapAddressDetails,
        onSuccessFetchAddress,
      );
    } else {
      this.setState({
        ...this.state,
        value: item.value,
        currentValue: item.value,
        isFetching: true,
        hasError: false,
      });

      await this.fetchAddress(this.getAddressesByIdEndpoint(item.id), item.value, this.mapAddresses, this.setAddresses);
    }
  };

  renderAddress = (item, isHighlighted) => {
    return (
      <div
        style={{ padding: '5px', backgroundColor: isHighlighted ? '#f5f5f5' : '#fff' }}
        className={`item ${isHighlighted ? 'item-highlighted' : ''}`}
        key={item.id}
      >
        {this.renderAddressItem(item)}
      </div>
    );
  };

  renderAddressItem = item => {
    const addressText =
      item.type === PostCodeAddressType.Address ? (
        <React.Fragment>
          {item.text} <br />
        </React.Fragment>
      ) : (
        ''
      );
    const addressDescription = <strong>{item.description}</strong>;
    return (
      <div>
        {addressText}
        {addressDescription}
      </div>
    );
  };

  renderAutocomplete = () => {
    const { disabled, showOnlyPostcode, oldValue } = this.props;
    const inputProps = {
      id: 'states-autocomplete',
      placeholder: locale.getMsg('searchPostcodePlaceholder'),
      autoComplete: 'off',
      name: 'postcode-search',
    };

    if (disabled) {
      inputProps.disabled = 'disabled';
    }

    return (
      <Autocomplete
        ref={this.autoCompleteRef}
        inputProps={inputProps}
        value={
          // eslint-disable-next-line no-nested-ternary
          showOnlyPostcode && !this.state.value ? (oldValue === null ? '' : this.state.currentValue) : this.state.value
        }
        items={this.state.addresses}
        getItemValue={item => (showOnlyPostcode ? item.value : item.description)}
        onSelect={this.autocompleteOnSelect}
        onChange={this.autoCompleteOnChange}
        renderMenu={children => <div className="postcode-menu">{children}</div>}
        renderItem={(item, isHighlighted) => this.renderAddress(item, isHighlighted)}
      />
    );
  };

  render() {
    return (
      <div className={`${this.state.hasError ? 'postcode-wrapper-error' : 'postcode-wrapper'}`}>
        {this.state.isFetching && <div className="postcode-loader">{locale.getMsg('searchPostcodeLoading')}</div>}
        <form noValidate>{this.renderAutocomplete()}</form>
      </div>
    );
  }
}

PostcodeSearch.defaultProps = {
  oldValue: '',
  showOnlyPostcode: false,
  onSelect: () => {},
  onInvalidPostcode: () => {},
  disabled: false,
};

PostcodeSearch.propTypes = {
  oldValue: PropTypes.string,
  showOnlyPostcode: PropTypes.bool,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  jwtToken: PropTypes.string.isRequired,
  handleApiError: PropTypes.func.isRequired,
};

export default PostcodeSearch;
